import { Link } from "react-router-dom";
import { PUBLIC_CODE_URL } from "../lib/flags";
import { AccountLink, useHomePath } from "../lib/session";
import { CheckIcon, CrossIcon, ExternalIcon, Logo } from "../lib/ui";

const KEPT = [
  "The amount",
  "The reference",
  "The payer’s name, as the bank printed it",
  "The sender’s address",
  "The email’s message ID, so one email is never counted twice",
];

const NEVER = [
  "The rest of the email. Only the details above are saved, never the message itself.",
  "Your bank login or password. ConfirmPay never asks for either.",
  "Any money. Payments go from the payer’s bank to yours, not through us.",
  "Access to your inbox. Only what your filter forwards ever arrives.",
];

export default function Trust() {
  const home = useHomePath();
  return (
    <div>
      <header className="wrap site-header">
        <Logo height={28} to={home} />
        <nav aria-label="Main" style={{ display: "flex", alignItems: "center", gap: 28, fontSize: 15, fontWeight: 600 }}>
          <Link to="/" className="plain-link hide-sm">Back to ConfirmPay</Link>
          <AccountLink className="plain-link">Sign in</AccountLink>
        </nav>
      </header>

      <main>
        {/* hero */}
        <section className="wrap" style={{ paddingTop: 56, paddingBottom: 88 }}>
          <h1 className="display" style={{ fontSize: "min(68px, 11vw)", maxWidth: 980 }}>
            What happens to a forwarded email, step by step.
          </h1>
          <p className="lead" style={{ marginTop: 26, maxWidth: 700 }}>
            You forward your bank&rsquo;s payment emails to ConfirmPay so it can tell when a payment lands. Here is exactly what it reads, what it keeps, and what stays in your hands.
          </p>
        </section>

        {/* pipeline */}
        <section className="wrap" style={{ paddingBottom: 104 }}>
          <div className="g4">
            <div className="stage">
              <div className="step-label">1</div>
              <h2 className="h3">Your bank emails you</h2>
              <p className="body">The same payment notice it always sends when money arrives. Nothing changes about your bank account.</p>
            </div>
            <div className="stage">
              <div className="step-label">2</div>
              <h2 className="h3">Your own filter forwards a copy</h2>
              <p className="body">The filter lives in your inbox and only matches your bank&rsquo;s sender addresses, so nothing else is forwarded.</p>
            </div>
            <div className="stage stage--hl">
              <div className="step-label" style={{ color: "var(--sage-ink)" }}>3</div>
              <h2 className="h3">ConfirmPay&rsquo;s code reads three things</h2>
              <p className="body" style={{ color: "var(--ink)" }}>The amount, the reference, and the payer&rsquo;s name as your bank prints it. There is no mailbox: the email is read the moment it arrives. No AI is involved.</p>
            </div>
            <div className="stage">
              <div className="step-label">4</div>
              <h2 className="h3">The request is marked paid</h2>
              <p className="body">If the reference matches a waiting request, it turns paid and the payer is emailed a receipt.</p>
            </div>
          </div>
        </section>

        {/* keeps / never keeps */}
        <section className="rule-top">
          <div className="wrap" style={{ paddingTop: 88, paddingBottom: 104 }}>
            <div className="g2 g2--lists">
              <div>
                <h2 className="h2" style={{ fontSize: "min(44px, 9vw)" }}>What ConfirmPay keeps</h2>
                <p className="body" style={{ margin: "14px 0 28px" }}>For each forwarded email, one short record, so a payment can be matched and never counted twice.</p>
                <div>
                  {KEPT.map((t) => (
                    <div className="li" key={t}>
                      <span style={{ color: "var(--sage-ink)" }}><CheckIcon size={22} /></span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="h2" style={{ fontSize: "min(44px, 9vw)" }}>What it never keeps</h2>
                <p className="body" style={{ margin: "14px 0 28px" }}>Everything else stays out of ConfirmPay.</p>
                <div>
                  {NEVER.map((t) => (
                    <div className="li" key={t}>
                      <span style={{ color: "var(--red-ink)" }}><CrossIcon size={22} /></span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* control */}
        <section className="rule-top">
          <div className="wrap" style={{ paddingTop: 88, paddingBottom: 104 }}>
            <h2 className="h2" style={{ fontSize: "min(44px, 9vw)", maxWidth: 640 }}>You stay in control.</h2>
            <div className="g3" style={{ marginTop: 44, gap: 40 }}>
              <div style={{ borderTop: "1px solid var(--line2)", paddingTop: 22 }}><h3 className="h3" style={{ fontSize: 22 }}>Stop it yourself</h3><p className="body" style={{ marginTop: 10 }}>Delete the filter in your own inbox and forwarding stops at once. You do not need to ask us.</p></div>
              <div style={{ borderTop: "1px solid var(--line2)", paddingTop: 22 }}><h3 className="h3" style={{ fontSize: 22 }}>Change the address</h3><p className="body" style={{ marginTop: 10 }}>Rotate your private forwarding address from your dashboard whenever you like. The old one stops working.</p></div>
              <div style={{ borderTop: "1px solid var(--line2)", paddingTop: 22 }}><h3 className="h3" style={{ fontSize: 22 }}>Limit the scope</h3><p className="body" style={{ marginTop: 10 }}>Your filter only matches your bank&rsquo;s senders. Nothing else in your inbox is touched or seen.</p></div>
            </div>
          </div>
        </section>

        {/* check the code — only once the public repository exists */}
        {PUBLIC_CODE_URL && (
          <section className="band">
            <div className="wrap" style={{ paddingTop: 88, paddingBottom: 88 }}>
              <div className="g2" style={{ alignItems: "center", gap: 48 }}>
                <div>
                  <h2 className="h2" style={{ fontSize: "min(44px, 9vw)", color: "var(--band-fg)" }}>Check what it does, line by line.</h2>
                  <p style={{ margin: "16px 0 0", fontSize: 18, lineHeight: 1.55, color: "var(--band-mute)", maxWidth: 520 }}>
                    The code that reads a forwarded email is public. It is short and plain, with no AI in it, so anyone can check what it reads and exactly what it passes on.
                  </p>
                </div>
                <div>
                  <a href={PUBLIC_CODE_URL} className="btn btn--onband" target="_blank" rel="noreferrer">
                    View the email-reading code
                    <ExternalIcon />
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="wrap site-footer" style={{ fontSize: 14, color: "var(--ink3)" }}>
        <span>ConfirmPay is free for Singapore nonprofits and small businesses.</span>
        <span style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <Link to="/privacy" style={{ color: "var(--ink)", fontWeight: 600 }}>Privacy</Link>
          <Link to="/terms" style={{ color: "var(--ink)", fontWeight: 600 }}>Terms</Link>
          <Link to="/" style={{ color: "var(--ink)", fontWeight: 600 }}>Back to ConfirmPay</Link>
        </span>
      </footer>
    </div>
  );
}
